import {
  DestroyRef,
  Injectable,
  Signal,
  computed,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import {
  selectLoadedCurrentUser,
  User,
  UserGroupNames,
} from 'pl-core-services';
import { getIsSiriusEnabled } from '@app/common/sirius-util';

export const MEDIA_PERMISSIONS_BANNER_DISMISSED_KEY =
  'media_permissions_banner_dismissed';

export type ShellBannerId = 'media_permissions';

@Injectable({ providedIn: 'root' })
export class BannerService {
  private readonly showMediaPermissions = signal(false);

  readonly activeShellBanner: Signal<ShellBannerId | null> = computed(() =>
    this.showMediaPermissions() ? 'media_permissions' : null
  );

  /** Resolved once the current user is known, so one user's dismissal on a
   * shared machine does not hide the banner for the next one. */
  private mediaPermissionsDismissedKey: string | null = null;

  constructor(private store: Store, private destroyRef: DestroyRef) {}

  load(): void {
    this.loadMediaPermissionsBanner();
  }

  dismissMediaPermissions(): void {
    if (this.mediaPermissionsDismissedKey) {
      localStorage.setItem(this.mediaPermissionsDismissedKey, '1');
    }
    this.showMediaPermissions.set(false);
  }

  private loadMediaPermissionsBanner(): void {
    if (!getIsSiriusEnabled()) {
      this.showMediaPermissions.set(false);
      return;
    }

    this.store
      .pipe(
        selectLoadedCurrentUser,
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((user) => {
        this.mediaPermissionsDismissedKey = `${MEDIA_PERMISSIONS_BANNER_DISMISSED_KEY}_${user.uuid}`;
        this.showMediaPermissions.set(
          localStorage.getItem(this.mediaPermissionsDismissedKey) === null &&
            this.hasRoomAccess(user)
        );
      });
  }

  private hasRoomAccess(user: User): boolean {
    return !!user.groups?.includes(UserGroupNames.SAAS_PROVIDER);
  }
}
